import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ChatMessage } from "@/types";
import { Paperclip, Flag, MoveHorizontal } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { TabellaChat } from "./TabellaChat";
import { useAuth } from "@/auth/useAuth";
import { useChat } from "./ChatContext";
import { ReportDialog } from "./ReportDialog";

// Custom link renderer: href "aef:choose:N" dispatches `la N` to the chat
// instead of navigating. Used by the cat plugin to make offer names
// clickable in the offerte table. href "aef:say:<testo-url-encoded>"
// dispatches the decoded text verbatim, used for multiple-choice options
// (motivo voltura, titolarità immobile).
function ChatLink({ href, children }: { href?: string; children?: React.ReactNode }) {
  const { sendMessage, isQr } = useChat();
  if (href && href.startsWith("aef:")) {
    const parts = href.split(":");
    return (
      <a
        href="#"
        title={isQr ? "Scelga questa offerta" : "Clicca per scegliere questa offerta"}
        className="font-semibold text-emerald-800 underline decoration-emerald-400 decoration-dashed underline-offset-2 hover:text-emerald-900 hover:decoration-emerald-700 hover:decoration-solid cursor-pointer transition-colors"
        onClick={(e) => {
          e.preventDefault();
          if (parts[1] === "choose" && parts[2]) {
            sendMessage(`la ${parts[2]}`);
          } else if (parts[1] === "say" && parts[2]) {
            sendMessage(decodeURIComponent(parts.slice(2).join(":")));
          }
        }}
      >
        {children}
      </a>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline">
      {children}
    </a>
  );
}

interface Props {
  messages: ChatMessage[];
  isLoading?: boolean;
}

// Wrapper che monitora overflow orizzontale e mostra badge "scorri →"
// quando il contenuto supera il bubble. Necessario perché Chrome macOS
// nasconde la scrollbar custom (system pref "show scrollbars when scrolling").
function MessageBody({ content, invert = true }: { content: string; invert?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflow(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [content]);

  return (
    <div className="relative">
      <div
        ref={ref}
        className={`chat-msg-prose prose prose-sm max-w-none overflow-x-auto ${invert ? "prose-invert" : "prose-slate"} [&>*:first-child]:mt-0 [&>*:last-child]:mb-1 [&_p]:mb-3 [&_table]:border-collapse [&_table]:mb-4 [&_table]:w-max [&_table]:max-w-none [&_table]:rounded-lg [&_table]:overflow-hidden [&_table]:bg-white [&_table]:shadow-md [&_thead]:bg-[#0F2818] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-[10px] [&_th]:font-bold [&_th]:tracking-wider [&_th]:uppercase [&_th]:text-emerald-200 [&_th]:border-none [&_td]:px-3 [&_td]:py-2 [&_td]:text-slate-800 [&_td]:border-b [&_td]:border-slate-200 [&_td]:text-[13px] [&_td]:tabular-nums [&_tbody_tr:last-child_td]:border-b-0 [&_tbody_tr:hover_td]:bg-emerald-50/70 [&_th:empty]:hidden [&_thead:has(th:empty)]:hidden`}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          urlTransform={(url) => (url.startsWith("aef:") ? url : url)}
          components={{ a: ChatLink as never, table: TabellaChat as never }}
        >
          {content}
        </Markdown>
      </div>
      {overflow && (
        <div className="pointer-events-none absolute bottom-1 right-1 flex items-center gap-1 rounded-full bg-black/40 px-2 py-0.5 text-[10px] font-medium text-white shadow-sm">
          <MoveHorizontal size={11} />
          scorri
        </div>
      )}
    </div>
  );
}

// Messaggio con la tabella delle offerte: sul telefono il fumetto prende tutta la larghezza,
// così le schede (TabellaChat) non restano strette all'80%.
function haOfferte(msg: ChatMessage): boolean {
  return msg.role === "assistant" && /\|\s*Prezzo energia\s*\|/.test(msg.content ?? "");
}

export function MessageList({ messages, isLoading }: Props) {
  const endRef = useRef<HTMLDivElement>(null);
  const { hasRole } = useAuth();
  const isStaff = hasRole("affiliato") || hasRole("operatore");
  const [reportTarget, setReportTarget] = useState<ChatMessage | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  return (
    <div className="@container flex-1 overflow-y-auto p-4 space-y-3">
      {messages.map((msg) => (
        <div
          key={msg.id}
          data-tour={msg.role === "assistant" ? "bot-message" : undefined}
          className={`group flex ${msg.role === "user" ? "justify-end" : "justify-start items-start gap-1"}`}
        >
          <div
            className={`${haOfferte(msg) ? "max-w-full @3xl:max-w-[80%]" : "max-w-[80%]"} px-4 py-2.5 rounded-xl text-sm ${
              msg.role === "assistant"
                ? "bg-slate-100 text-slate-900 border border-slate-200 rounded-bl-sm"
                : "bg-brand-dark text-white rounded-br-sm"
            }`}
          >
            {msg.attachment && (
              <div className="flex items-center gap-1.5 text-xs opacity-90">
                <Paperclip size={12} />
                <span className="truncate max-w-[200px]">{msg.attachment.name}</span>
              </div>
            )}
            {msg.content && (
              <MessageBody content={msg.content} invert={msg.role === "user"} />
            )}
          </div>
          {isStaff && msg.role === "assistant" && msg.content && (
            <button
              type="button"
              data-tour="flag"
              onClick={() => setReportTarget(msg)}
              className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity mt-1 p-1 text-text/50 hover:text-red-600"
              title="Segnala risposta anomala"
              aria-label="Segnala"
            >
              <Flag size={14} />
            </button>
          )}
        </div>
      ))}
      {isLoading && (
        <div className="flex justify-start">
          <div className="bg-slate-100 text-slate-500 border border-slate-200 px-4 py-2.5 rounded-xl rounded-bl-sm text-sm">
            <span className="inline-flex gap-1">
              <span className="animate-bounce" style={{ animationDelay: "0ms" }}>.</span>
              <span className="animate-bounce" style={{ animationDelay: "150ms" }}>.</span>
              <span className="animate-bounce" style={{ animationDelay: "300ms" }}>.</span>
            </span>
          </div>
        </div>
      )}
      <div ref={endRef} />
      <ReportDialog
        open={reportTarget !== null}
        messageContent={reportTarget?.content || ""}
        messageTimestamp={reportTarget?.timestamp}
        onClose={() => setReportTarget(null)}
      />
    </div>
  );
}
